import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useToast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { FulfillmentStateIcon } from "@/services/backend/relationships/components/fulfillment-state-icon";
import { workOrderStateActionLabel } from "@/services/backend/relationships/lib/fulfillment-state";

/**
 * UpdateWorkOrderStateRequest is the request every work order takes to move to
 * another {@link FulfillmentState}. The document state of the work order is
 * derived from it in the backend and recorded for the requesting user, which
 * the policy enforces.
 */
export interface UpdateWorkOrderStateRequest {
  id: string;
  workOrderState: FulfillmentState;
  stateSetBy: string;
}

type UpdateWorkOrderStateMutation = () => readonly [
  (request: UpdateWorkOrderStateRequest) => void,
  {
    isLoading: boolean;
    error?: unknown;
    isSuccess: boolean;
    reset: () => void;
  },
];

/**
 * WorkOrderStateDropdown shows the state of a work order and offers the
 * transitions it allows, like the DocumentStateDropdown does for the lifecycle
 * state of a document.
 *
 * Which transitions a work order allows depends on the page it is shown on -
 * planning offers other steps than execution - so the caller passes them in
 * `transitions`, in the order they are to be shown. Without any, the state is
 * shown as a disabled button.
 *
 * Cancelling is confirmed in a dialog; the other transitions are a normal part
 * of working on a work order and are applied right away.
 */
export function WorkOrderStateDropdown({
  workOrder,
  mutation,
  transitions,
  onError,
}: {
  workOrder: { id: string; workOrderState: FulfillmentState };
  mutation: UpdateWorkOrderStateMutation;
  transitions: FulfillmentState[];
  /**
   * onError receives the error of a rejected transition, except a
   * validation or bsm.ErrDocumentNotReady error, which is written into the
   * nearest ErrValidationFailedContextProvider. Without it every other error is
   * shown as a toast.
   */
  onError?: (error: unknown) => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { write: writeValidationError } = useErrValidationFailedContext();
  const [confirming, setConfirming] = useState(false);
  const [mutate, { isLoading, error, isSuccess, reset }] = mutation();

  useEffect(() => {
    if (isSuccess) {
      reset();
      setConfirming(false);
    }
  }, [isSuccess, reset]);

  // The dropdown stays mounted while the user works on the work order, so an
  // error is reported once. Reporting it on every render would restore the
  // errors the user is currently resolving.
  const reportedError = useRef<unknown>(null);
  useEffect(() => {
    if (!error) {
      reportedError.current = null;
      return;
    }

    if (reportedError.current === error) {
      return;
    }
    reportedError.current = error;

    // A work order which is not ready for the customer names the fields it is
    // missing. They belong on the page, where the user resolves them, not in a
    // toast which cannot show them. Without a provider the error is toasted.
    if (writeValidationError(error)) {
      return;
    }

    if (onError) {
      onError(error);
      return;
    }

    toast({ ...parseRTKQueryError(error, false), variant: "destructive" });
  }, [error, onError, writeValidationError, toast]);

  const updateState = (state: FulfillmentState) => {
    if (isLoading) {
      return;
    }

    mutate({
      id: workOrder.id,
      workOrderState: state,
      stateSetBy: user?.id || "",
    });
  };

  if (transitions.length === 0) {
    return (
      <Button variant="outline" disabled>
        <FulfillmentStateIcon
          state={workOrder.workOrderState}
          className="mr-2 size-4"
          disableTooltip
        />
        <span>{t(workOrder.workOrderState)}</span>
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" disabled={isLoading}>
            <FulfillmentStateIcon
              state={workOrder.workOrderState}
              className="mr-2 size-4"
              disableTooltip
            />
            <span>{t(workOrder.workOrderState)}</span>
            <ChevronDown className="ml-2 size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {transitions.map((state) => (
            <DropdownMenuItem
              key={state}
              onClick={() => {
                // Drop the error of an earlier attempt, so the dialog and the
                // page show what this attempt reports.
                reset();
                if (state === FulfillmentState.Cancelled) {
                  setConfirming(true);
                  return;
                }
                updateState(state);
              }}
            >
              <FulfillmentStateIcon
                state={state}
                className="mr-2 size-4"
                disableTooltip
              />
              {workOrderStateActionLabel(state)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Arbeitsschein stornieren?")}</DialogTitle>
            <DialogDescription>
              {t(
                "Der Arbeitsschein wird als storniert markiert. Er wird dann nicht mehr in der Planung oder Ausführung angezeigt.",
              )}
            </DialogDescription>
          </DialogHeader>
          <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("Abbrechen")}</Button>
            </DialogClose>
            <Button
              disabled={isLoading}
              onClick={() => updateState(FulfillmentState.Cancelled)}
            >
              {t("Stornieren")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
